import dayjs, { Dayjs } from 'dayjs';

import tzData from '../resources/data/timezones.json';

// Plugin extensions (utc, timezone, isSameOrAfter, isSameOrBefore, customParseFormat)
// live in src/utils/dayjs-setup.ts which is loaded eagerly via root-wrapper.tsx and jest.setup.ts.

// captures $1=date, $2=time, $3=timezone offset
const ISO_STRING_REGEX = /(\d{4}-[01]\d-[0-3]\d)T([0-2]\d:[0-5]\d(?::[0-5]\d)?(?:\.\d+)?)([+-][0-2]\d:[0-5]\d|Z)/;

function padZeroTime(num: number, prefix = false) {
  if (num < 0) {
    return `${prefix ? '-' : ''}${num > -10 ? '0' : ''}${Math.abs(num)}`;
  }
  return `${prefix ? '+' : ''}${num < 10 ? '0' : ''}${num}`;
}

export function getTimeZoneByAbbr(abbr: string) {
  return tzData.timezones.find((timezone) => timezone.abbr === abbr);
}

export function getTimeZoneByName(name: string) {
  return tzData.timezones.find((timezone) => timezone.value === name);
}

export function getLocalTimezone() {
  const value = new Intl.DateTimeFormat('en-US', { timeZoneName: 'long' }).format(new Date()).split(', ')[1];
  return getTimeZoneByName(value);
}

export function getTimeAsUTC(time: string) {
  const localTime = dayjs(time).utc(true); // Set datetime as UTC without changing value
  return localTime || time;
}

export const toJsTimeZoneName = (ianaZone: string, date?: Date): string =>
  new Intl.DateTimeFormat('en-US', { timeZone: ianaZone, timeZoneName: 'long' }).format(date).split(', ')[1];

export function getTimeZoneAbbreviation(ISOString: string, origTimezoneName?: string) {
  if (!ISOString) {
    return ISOString;
  }

  let timezoneName = origTimezoneName;
  // show local timezone short abbr
  if (!timezoneName) {
    timezoneName = new Intl.DateTimeFormat('en-US', { timeZoneName: 'long' })
      .format(new Date(ISOString))
      .split(', ')[1];
  }

  const tz = getTimeZoneByName(timezoneName);
  return tz && tz.abbr;
}

// dayjs does not support timezone, add timezone abbr manually
export function formatDate(origValue: string, format = 'MMM DD, YYYY h:mm A Z', timezoneName?: string) {
  if (origValue) {
    const value = dayjs(origValue).toISOString();
    const tzOffsetRegex = /\sZ$/;
    const hasTzOffsetRegex = tzOffsetRegex.test(format);
    // if format pattern is w/o timezone, dayjs can handle it
    if (!hasTzOffsetRegex && !timezoneName) {
      return dayjs(value).format(format);
    }

    const formatWithoutOffset = format.replace(tzOffsetRegex, '');
    // remove timezone offset and format
    const tz = timezoneName && getTimeZoneByName(timezoneName);
    const dateStr = dayjs(tz ? value.replace(ISO_STRING_REGEX, '$1T$2') : value)
      .add(tz ? tz.offset : 0, 'hour')
      .format(formatWithoutOffset);

    if (!hasTzOffsetRegex) {
      return dateStr;
    }

    const timezoneStr = getTimeZoneAbbreviation(value, timezoneName);
    if (!timezoneStr) {
      return dateStr;
    }

    return `${dateStr} ${timezoneStr}`;
  } else {
    return '';
  }
}

export function formatDateWithoutTime(origValue: string, timezoneName?: string) {
  return formatDate(origValue, 'YYYY-MM-DD', timezoneName);
}

const getTimeZone = (timezoneName: string) => {
  return getTimeZoneByName(timezoneName) || getTimeZoneByName(timezoneName.replace(' Standard', ''));
};

export function getCombinedDateStr(dateStr?: string, timezoneName = 'Greenwich Mean Time', returnShort = false) {
  if (!dateStr) {
    return dateStr;
  }

  // test against `2020-04-10T12:34` format
  const result = /(\d{4}-[01]\d-[0-3]\d)(T[0-2]\d:[0-5]\d(?::[0-5]\d)?(?:\.\d+)?)?/.exec(dateStr);

  // if date portion is not detected, return original string
  if (!result || !result[1]) {
    return dateStr;
  }

  let finalStr = dateStr;

  // if time portion is not detected, set the default time
  if (!result[2]) {
    finalStr += 'T00:00';
  }
  // we need to have work around here, as Standard replace, because of IANA timezones
  const tz = getTimeZone(timezoneName);
  const offset = tz && tz.offset;
  if (offset !== undefined) {
    const hours = parseInt(`${offset}`, 10);
    const minutes = (offset * 60) % 60;

    if (returnShort) {
      return finalStr;
    }

    finalStr = `${finalStr}${padZeroTime(hours, true)}:${padZeroTime(minutes)}`;
  }

  return finalStr;
}

/**
 * Takes a given date, ignores its set timezone, and applies given timezone.
 * Returned date is in UTC.
 */
export const applyTimezoneToDayjs = (date: Dayjs, timezoneName: string): Dayjs => {
  if (getTimeZone(timezoneName)?.offset === undefined) {
    return date;
  }

  const dateStr = getCombinedDateStr(date.format('YYYY-MM-DDTHH:mm'), timezoneName);
  const result = dayjs.utc(dateStr);

  return result;
};

/**
 * Compares two Dayjs objects and returns true if first date is before or equal to the second
 * If no comparison date is passed, then the time now is used
 */
export const isDateBeforeOrSame = (date: Dayjs, comparisonDate: Dayjs = dayjs()): boolean => {
  return date.isBefore(comparisonDate) || date.isSame(comparisonDate);
};

export function extractTimeStr(ISOString?: string) {
  if (!ISOString) {
    return ISOString;
  }
  const result = ISO_STRING_REGEX.exec(ISOString);
  return result ? result[2] : undefined;
}

export function removeTimezoneOffset(ISOString?: string) {
  if (!ISOString) {
    return ISOString;
  }
  return ISOString.replace(ISO_STRING_REGEX, '$1T$2');
}

/**
 * Returns ISO string with only date and time against the given timezone
 *
 * @export
 * @param {string} dateStr
 * @param {string} timezoneName
 * @returns
 */
export function getValueWithoutTimezone(dateStr: string, timezoneName: string) {
  if (!dateStr) {
    return dateStr;
  }
  const { offset = 0 } = getTimeZoneByName(timezoneName) || {};
  const dateValue = dayjs(dateStr).add(offset, 'hour');
  const utcISOValue = dateValue.toISOString();

  return removeTimezoneOffset(utcISOValue);
}

/**
 * @param {string|Dayjs} start
 * @param {string|Dayjs} end
 * @returns {Dayjs[]}
 */
export const getDateRange = (start: string | Dayjs, end: string | Dayjs): Dayjs[] => {
  const s = dayjs.isDayjs(start) ? start : dayjs(start);
  const e = dayjs.isDayjs(end) ? end : dayjs(end);
  return [...Array(1 + e.diff(s, 'days')).keys()].map((n) => dayjs(s).add(n, 'days'));
};

/**
 * This does not check all the valid ISO format, just based on the regex we define as `ISO_STRING_REGEX`
 *
 * @export
 * @param {string} [ISOString]
 * @returns
 */
export function isISOString(ISOString?: string) {
  if (!ISOString) {
    return false;
  }
  return ISO_STRING_REGEX.test(ISOString);
}

export function diffInHours(start: any, end: any) {
  return dayjs(end).diff(dayjs(start), 'h');
}

export const areValidDates = (startDate?: string, endDate?: string, timezone?: string): boolean => {
  if (!startDate || !endDate) {
    return false;
  }
  const now = dayjs();
  const formattedStartDate = dayjs(getCombinedDateStr(startDate, timezone));
  const formattedEndDate = dayjs(getCombinedDateStr(endDate, timezone));

  return !!(startDate && endDate && formattedStartDate > now && formattedEndDate > formattedStartDate);
};

/**
 * Returns the date range in a special format: MMMM DD-DD, YYYY
 *
 * @export
 * @param {string} start
 * @param {string} end
 * @param {Record<string, any>} [formats]
 * @returns {string}
 */
export const formatDateRangeToMonthDayDayYear = (start: string, end: string, formats?: Record<string, any>): string => {
  const { month, day, year } = {
    month: 'MMMM',
    day: 'DD',
    year: 'YYYY',
    ...formats,
  };
  const startFormat =
    !dayjs(start).isSame(end, 'day') && dayjs(start).isSame(end, 'year')
      ? `${month} ${day}`
      : `${month} ${day}, ${year}`;
  let endFormat = '';

  if (!dayjs(start).isSame(end, 'day')) {
    if (dayjs(start).isSame(end, 'month')) {
      endFormat += day;
    } else {
      endFormat += `${month} ${day}`;
    }
    endFormat += `, ${year}`;
  }

  return endFormat ? `${formatDate(start, startFormat)}-${formatDate(end, endFormat)}` : formatDate(start, startFormat);
};
